import * as THREE from 'three';
import type { MapDefinition } from '../../../shared/game/world-types.js';
import { terrainCellAt, terrainLatticeHeight, terrainMesh } from '../../../shared/game/terrain.js';

type AcceptGround = (point: THREE.Vector3) => boolean;
type Query = (raycaster: THREE.Raycaster, hits: THREE.Intersection[], accept?: AcceptGround) => void;
const queries = new WeakMap<THREE.Mesh, Query>();
type Surface = (x: number, z: number, height: number) => number;
const surfaces = new WeakMap<THREE.Mesh, Surface>();

/** Picks the ground where it is drawn when a presentation layer moves it (an interior's sunk rock), not at its real height. */
export function setTerrainSurface(mesh: THREE.Mesh, surface: Surface | null) {
  if (surface) surfaces.set(mesh, surface); else surfaces.delete(mesh);
}

/** Whether the mesh has lattice picking; any other ground is flat and only reachable by brute-force raycasts. */
export const hasTerrainPicking = (mesh: THREE.Mesh) => queries.has(mesh);

/** The first revealed surface along the ray. If no ground exists behind an opening, its original surface stays solid. */
export function pickTerrainGround(mesh: THREE.Mesh, raycaster: THREE.Raycaster, accept?: AcceptGround): THREE.Vector3 | null {
  const query = queries.get(mesh);
  if (!query) return raycaster.intersectObject(mesh, false).find(hit => !accept || accept(hit.point))?.point ?? null;
  const hits: THREE.Intersection[] = [];
  let fallback: THREE.Vector3 | null = null;
  query(raycaster, hits, point => {
    if (!fallback) fallback = point.clone();
    return !accept || accept(point);
  });
  return hits[0]?.point ?? fallback;
}

/**
 * Raycast the actual ground triangles by traversing only the lattice cells under the ray. Each cell is tested as the mesh
 * cell that covers it (a coarse cell spans several), built from the same lattice heights as the mesh.
 */
export function enableTerrainPicking(mesh: THREE.Mesh, map: MapDefinition) {
  const { columns, rows, dx, dz } = terrainMesh(map);
  mesh.geometry.computeBoundingBox(); const box = mesh.geometry.boundingBox!;
  const ray = new THREE.Ray(), inverse = new THREE.Matrix4();
  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3(), d = new THREE.Vector3(), hit = new THREE.Vector3(), second = new THREE.Vector3();
  const corner = (target: THREE.Vector3, i: number, j: number) => {
    const x = i * dx - map.width / 2, z = j * dz - map.depth / 2, height = terrainLatticeHeight(map, i, j), surface = surfaces.get(mesh);
    return target.set(x, surface ? surface(x, z, height) : height, z);
  };
  const query: Query = (raycaster, hits, accept) => {
    inverse.copy(mesh.matrixWorld).invert(); ray.copy(raycaster.ray).applyMatrix4(inverse);
    let begin = 0, end = Infinity;
    for (const axis of ['x', 'y', 'z'] as const) {
      const origin = ray.origin[axis], direction = ray.direction[axis];
      if (Math.abs(direction) < 1e-12) { if (origin < box.min[axis] || origin > box.max[axis]) return; continue; }
      const t0 = (box.min[axis] - origin) / direction, t1 = (box.max[axis] - origin) / direction;
      begin = Math.max(begin, Math.min(t0, t1)); end = Math.min(end, Math.max(t0, t1));
    }
    if (raycaster.near > 0) begin = Math.max(begin, raycaster.ray.at(raycaster.near, second).applyMatrix4(inverse).distanceTo(ray.origin));
    if (Number.isFinite(raycaster.far)) end = Math.min(end, raycaster.ray.at(raycaster.far, second).applyMatrix4(inverse).distanceTo(ray.origin));
    if (begin > end || !Number.isFinite(begin)) return;
    ray.at(begin, hit);
    let ix = Math.max(0, Math.min(columns - 1, Math.floor((hit.x + map.width / 2) / dx))), iz = Math.max(0, Math.min(rows - 1, Math.floor((hit.z + map.depth / 2) / dz)));
    const sx = ray.direction.x >= 0 ? 1 : -1, sz = ray.direction.z >= 0 ? 1 : -1;
    let nextX = Math.abs(ray.direction.x) < 1e-12 ? Infinity : ((ix + (sx > 0 ? 1 : 0)) * dx - map.width / 2 - ray.origin.x) / ray.direction.x;
    let nextZ = Math.abs(ray.direction.z) < 1e-12 ? Infinity : ((iz + (sz > 0 ? 1 : 0)) * dz - map.depth / 2 - ray.origin.z) / ray.direction.z;
    const deltaX = dx / Math.abs(ray.direction.x), deltaZ = dz / Math.abs(ray.direction.z);
    let tested = -1;
    for (let step = 0; step <= columns + rows && ix >= 0 && ix < columns && iz >= 0 && iz < rows; step++) {
      const cell = terrainCellAt(map, ix, iz), key = cell.j * (columns + 1) + cell.i;
      // A coarse cell is crossed in several lattice steps; test its triangles once.
      if (key !== tested) {
        tested = key;
        corner(a, cell.i, cell.j); corner(b, cell.i, cell.j + cell.size); corner(c, cell.i + cell.size, cell.j + cell.size); corner(d, cell.i + cell.size, cell.j);
        const one = ray.intersectTriangle(a, b, d, true, hit), two = ray.intersectTriangle(b, c, d, true, second);
        if (one || two) {
          const candidates = [one, two].filter((point): point is THREE.Vector3 => !!point)
            .sort((a, b) => ray.origin.distanceToSquared(a) - ray.origin.distanceToSquared(b));
          for (const candidate of candidates) {
            const world = candidate.clone().applyMatrix4(mesh.matrixWorld), distance = raycaster.ray.origin.distanceTo(world);
            if (distance < raycaster.near || distance > raycaster.far || accept && !accept(world)) continue;
            hits.push({ distance, point: world, object: mesh }); return;
          }
        }
      }
      if (Math.min(nextX, nextZ) > end + 1e-8) return;
      if (nextX < nextZ) { ix += sx; nextX += deltaX; } else { iz += sz; nextZ += deltaZ; }
    }
  };
  queries.set(mesh, query);
  mesh.raycast = (raycaster, hits) => query(raycaster, hits);
}
